import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, Upload, Send } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-gray-100 text-gray-700',
  assigned: 'bg-blue-100 text-blue-700',
  contacted: 'bg-cyan-100 text-cyan-700',
  under_review: 'bg-amber-100 text-amber-700',
  candidate_search: 'bg-purple-100 text-purple-700',
  interviews: 'bg-indigo-100 text-indigo-700',
  offer_sent: 'bg-pink-100 text-pink-700',
  placement_completed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function RecruitmentRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRequest = async () => {
    try {
      const res = await api.get(`/recruitment/requests/${id}`);
      setReq(res.data);
    } catch {
      toast.error('Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      await api.post(`/recruitment/requests/${id}/comments`, { text: commentText.trim() });
      setCommentText('');
      fetchRequest();
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const uploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/recruitment/requests/${id}/documents?category=other`, fd);
      toast.success('Document uploaded');
      fetchRequest();
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] max-w-3xl mx-auto px-4 py-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!req) return null;

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/employer/recruitment-requests')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />Back to My Requests
        </button>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle>{req.job_title}</CardTitle>
                <p className="text-sm text-muted-foreground font-mono mt-1">{req.reference_code}</p>
              </div>
              <Badge className={STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-700'}>
                {(req.status || '').replace(/_/g, ' ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Location:</span> {req.location}</div>
              <div><span className="text-muted-foreground">Positions:</span> {req.positions_count}</div>
              <div><span className="text-muted-foreground">Employment Type:</span> {req.employment_type}</div>
              <div><span className="text-muted-foreground">Salary Range:</span> {req.salary_range || 'N/A'}</div>
              <div><span className="text-muted-foreground">Placement Fee:</span> {req.fee_percentage}% of first annual income</div>
              <div><span className="text-muted-foreground">Submitted:</span> {req.created_at?.slice(0, 10)}</div>
            </div>
            <p className="text-sm bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{req.job_description}</p>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base">Documents</CardTitle></CardHeader>
          <CardContent>
            {(req.documents || []).length === 0 ? (
              <p className="text-sm text-muted-foreground mb-3">No documents uploaded yet.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {req.documents.map((doc, i) => (
                  <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#003366] hover:underline">
                    <FileText className="w-4 h-4" />{doc.name}
                    <Download className="w-3.5 h-3.5 ml-auto" />
                  </a>
                ))}
              </div>
            )}
            <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={uploadDocument} />
            <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-2" />{uploading ? 'Uploading...' : 'Upload Additional Document'}
            </Button>
          </CardContent>
        </Card>

        {/* Timeline */}
        {(req.timeline || []).length > 0 && (
          <Card className="mb-6">
            <CardHeader><CardTitle className="text-base">Progress Timeline</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {[...req.timeline].reverse().map((t, i) => (
                <div key={i} className="text-sm flex items-start gap-2">
                  <span className="text-muted-foreground shrink-0">{t.created_at?.slice(0, 16).replace('T', ' ')}</span>
                  <span className="font-medium">{t.event?.replace(/_/g, ' ')}</span>
                  {t.note && <span className="text-muted-foreground">— {t.note}</span>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Comments */}
        <Card>
          <CardHeader><CardTitle className="text-base">Comments</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {(req.comments || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No comments yet.</p>
            ) : (
              <div className="space-y-3">
                {req.comments.map((c, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium">{c.author_name}</p>
                      <p className="text-xs text-muted-foreground">{c.created_at?.slice(0, 16).replace('T', ' ')}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{c.text}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                className="flex-1 border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#003366]/20"
                placeholder="Add a comment or respond to your recruiter..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }}
              />
              <Button size="sm" disabled={submittingComment || !commentText.trim()} onClick={submitComment}>
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
